import { NumberInput, Select, TextInput } from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { MRT_ColumnDef } from 'mantine-react-table';
import { useState } from 'react';

import { provinceOptions } from 'constants/provinceConstants';
import { Payor, PayorType } from 'interfaces';

export const PayorColumns = () => {
  const { translate } = useTranslation();
  const columns: MRT_ColumnDef<Payor>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      Edit: ({ cell }) => {
        const [name, setName] = useState<string>(
          (cell.getValue() as string) || null
        );
        return (
          <TextInput
            label="Name"
            value={name}
            placeholder="Name"
            onChange={(event) => {
              const value = event.currentTarget.value;
              setName(value);
              cell.row._valuesCache.name = value;
            }}
            required
          />
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      Edit: ({ cell }) => {
        const [type, setType] = useState<string | null>(
          (cell.getValue() as PayorType) || null
        );
        return (
          <Select
            label="Type"
            value={type}
            data={Object.values(PayorType)}
            onChange={(value) => {
              setType(value);
              cell.row._valuesCache.type = value;
            }}
            required
          />
        );
      },
    },
    {
      accessorKey: 'billingNumberMinLength',
      header: translate(
        'pages:payors.billingNumberValidation.minLength',
        'Billing # minimum length'
      ),
      Edit: ({ cell }) => {
        const [minLength, setMinLength] = useState<string | number>(
          (cell.getValue() as number | null) ?? ''
        );
        return (
          <NumberInput
            label={translate(
              'pages:payors.billingNumberValidation.minLength',
              'Billing # minimum length'
            )}
            value={minLength}
            min={1}
            max={255}
            allowDecimal={false}
            allowNegative={false}
            onChange={(value) => {
              setMinLength(value);
              cell.row._valuesCache.billingNumberMinLength =
                typeof value === 'number' ? value : null;
            }}
          />
        );
      },
    },
    {
      accessorKey: 'billingNumberMaxLength',
      header: translate(
        'pages:payors.billingNumberValidation.maxLength',
        'Billing # maximum length'
      ),
      Edit: ({ cell }) => {
        const [maxLength, setMaxLength] = useState<string | number>(
          (cell.getValue() as number | null) ?? ''
        );
        return (
          <NumberInput
            label={translate(
              'pages:payors.billingNumberValidation.maxLength',
              'Billing # maximum length'
            )}
            value={maxLength}
            min={1}
            max={255}
            allowDecimal={false}
            allowNegative={false}
            onChange={(value) => {
              setMaxLength(value);
              cell.row._valuesCache.billingNumberMaxLength =
                typeof value === 'number' ? value : null;
            }}
          />
        );
      },
    },
    {
      accessorKey: 'billingNumberFormat',
      header: translate(
        'pages:payors.billingNumberValidation.format',
        'Billing # format'
      ),
      Edit: ({ cell }) => {
        const [format, setFormat] = useState<string>(
          (cell.getValue() as string | null) ?? 'ANY'
        );
        return (
          <Select
            label={translate(
              'pages:payors.billingNumberValidation.format',
              'Billing # format'
            )}
            value={format}
            data={[
              {
                value: 'ANY',
                label: translate(
                  'pages:payors.billingNumberValidation.formats.any',
                  'Any characters'
                ),
              },
              {
                value: 'NUMERIC',
                label: translate(
                  'pages:payors.billingNumberValidation.formats.numeric',
                  'Numbers only'
                ),
              },
              {
                value: 'ALPHANUMERIC',
                label: translate(
                  'pages:payors.billingNumberValidation.formats.alphanumeric',
                  'Letters and numbers only'
                ),
              },
            ]}
            onChange={(value) => {
              const nextFormat = value ?? 'ANY';
              setFormat(nextFormat);
              cell.row._valuesCache.billingNumberFormat = nextFormat;
            }}
            required
          />
        );
      },
    },
    {
      accessorKey: 'fax',
      header: 'Fax',
      Edit: ({ cell }) => {
        const [fax, setFax] = useState<string>(
          (cell.getValue() as string) || null
        );
        return (
          <TextInput
            label="Fax"
            value={fax}
            placeholder="Fax"
            onChange={(event) => {
              const value = event.currentTarget.value;
              setFax(value);
              cell.row._valuesCache.fax = value;
            }}
          />
        );
      },
      mantineEditTextInputProps: {
        placeholder: 'Fax',
        defaultValue: null,
      },
    },
    {
      accessorKey: 'province',
      accessorFn: (row) => row.province || null,
      header: 'Province',
      Edit: ({ cell }) => {
        const [province, setProvince] = useState<string | null>(
          (cell.getValue() as string) || null
        );
        return (
          <Select
            label="Province"
            value={province}
            placeholder="Province"
            data={provinceOptions}
            clearable
            onChange={(value) => {
              setProvince(value);
              cell.row._valuesCache.province = value;
            }}
          />
        );
      },
      mantineEditTextInputProps: {
        placeholder: 'Province',
        defaultValue: null,
      },
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
      Edit: ({ cell }) => {
        const [phone, setPhone] = useState<string>(
          (cell.getValue() as string) || null
        );
        return (
          <TextInput
            label="Phone"
            value={phone}
            placeholder="Phone"
            onChange={(event) => {
              const value = event.currentTarget.value;
              setPhone(value);
              cell.row._valuesCache.phone = value;
            }}
          />
        );
      },
      mantineEditTextInputProps: {
        placeholder: 'Phone',
        defaultValue: null,
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      Edit: ({ cell }) => {
        const [email, setEmail] = useState<string>(
          (cell.getValue() as string) || null
        );
        return (
          <TextInput
            label="Email"
            value={email}
            placeholder="Email"
            onChange={(event) => {
              const value = event.currentTarget.value;
              setEmail(value);
              cell.row._valuesCache.email = value;
            }}
          />
        );
      },
      mantineEditTextInputProps: {
        placeholder: 'Email',
        defaultValue: null,
      },
    },
    {
      accessorKey: 'note',
      header: 'Note',
      Edit: ({ cell }) => {
        const [note, setNote] = useState<string>(
          (cell.getValue() as string) || null
        );
        return (
          <TextInput
            label="Note"
            value={note}
            placeholder="Note"
            onChange={(event) => {
              const value = event.currentTarget.value;
              setNote(value);
              cell.row._valuesCache.note = value;
            }}
          />
        );
      },
      mantineEditTextInputProps: {
        placeholder: 'Note',
        defaultValue: null,
      },
    },
  ];

  return columns;
};
